import { db, isQuotaError } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  setDoc, 
  doc, 
  serverTimestamp,
  updateDoc,
  addDoc,
  deleteDoc
} from 'firebase/firestore';

export const syncHolidayToRegular = async (
  targetId: string, 
  targetType: string, 
  date: string, 
  status: 'present' | 'absent',
  additionalData?: any
) => {
  try {
    if (targetType === 'student') {
      console.log(`Syncing student ${targetId} for date ${date} with status ${status}`);
      // Find student to get classId
      const { getDoc } = await import('firebase/firestore');
      const studentDoc = await getDoc(doc(db, 'students', targetId));
      
      if (!studentDoc.exists()) {
        console.log(`Student ${targetId} not found in students collection`);
        return;
      }
      
      const studentData = studentDoc.data();
      const classId = studentData.classId;
      const className = studentData.className;

      if (!classId) {
        console.log(`Student ${targetId} has no classId assigned`);
        return;
      }

      // Match the ID structure used in Attendance.tsx
      const attendanceId = `${date}_${classId}_${targetId}`;
      console.log(`Saving to studentAttendance with ID: ${attendanceId}`);
      
      const attendanceData = {
        date,
        classId,
        className: className || '',
        studentId: targetId,
        studentName: studentData.fullName,
        present: status === 'present',
        notes: 'דווח דרך לשונית חופשים',
        updatedBy: 'system_sync',
        updatedAt: new Date().toISOString(),
        timestamp: serverTimestamp()
      };
      
      await setDoc(doc(db, 'studentAttendance', attendanceId), attendanceData);
    } else if (targetType === 'educational' || targetType === 'assistant' || targetType === 'substitute') {
      // For staff, if absent, create/update staffAbsences
      if (status === 'absent') {
        const { getDoc } = await import('firebase/firestore');
        const staffDoc = await getDoc(doc(db, 'staff', targetId));
        let staffData;
        let staffType = targetType;
        
        if (!staffDoc.exists()) {
          // Check substitutes
          const subDoc = await getDoc(doc(db, 'substitutes', targetId));
          if (!subDoc.exists()) return;
          const subData = subDoc.data();
          staffData = {
            fullName: `${subData.firstName} ${subData.lastName}`,
            nationalId: subData.nationalId
          };
          staffType = subData.type;
        } else {
          staffData = staffDoc.data();
        }

        const q = query(
          collection(db, 'staffAbsences'),
          where('staffId', '==', targetId),
          where('startDate', '<=', date),
          where('endDate', '>=', date)
        );
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          await addDoc(collection(db, 'staffAbsences'), {
            staffId: targetId,
            staffName: staffData.fullName,
            staffType: staffType,
            nationalId: staffData.nationalId,
            startDate: date,
            endDate: date,
            reason: 'קייטנה/חופש',
            substitute: '',
            notes: 'דווח דרך לשונית חופשים',
            timestamp: serverTimestamp()
          });
        }
      } else if (status === 'present') {
        const q = query(
          collection(db, 'staffAbsences'),
          where('staffId', '==', targetId),
          where('startDate', '<=', date),
          where('endDate', '>=', date)
        );
        const snapshot = await getDocs(q);
        
        for (const docSnap of snapshot.docs) {
          const absenceData = docSnap.data();
          if (absenceData.startDate === date && absenceData.endDate === date) {
            await deleteDoc(doc(db, 'staffAbsences', docSnap.id));
          } else {
            await updateDoc(doc(db, 'staffAbsences', docSnap.id), {
              notes: (absenceData.notes || '') + ` (נוכח בקייטנה בתאריך ${date})`
            });
          }
        }
      }
    } else if (targetType === 'transportLine' || targetType === 'transporter') {
      const attendanceId = `transport_${date}`;
      const { getDoc } = await import('firebase/firestore');
      const attendanceDoc = await getDoc(doc(db, 'transportAttendance', attendanceId));
      
      // Determine if this date is a holiday to set correct defaults
      const holidaySnapshot = await getDocs(query(
        collection(db, 'holidays'),
        where('startDate', '<=', date)
      ));
      const holiday = holidaySnapshot.docs.find(d => d.data().endDate >= date);
      const isHoliday = !!holiday;

      let displayName = targetId;
      if (targetType === 'transportLine') {
        const lineDoc = await getDoc(doc(db, 'transportLines', targetId));
        if (lineDoc.exists()) {
          displayName = lineDoc.data().name;
        }
      }

      if (attendanceDoc.exists()) {
        const data = attendanceDoc.data();
        const transporters = data.transporters || [];
        const index = transporters.findIndex((t: any) => t.id === targetId);
        
        if (index >= 0) {
          transporters[index].arrived = status === 'present';
          // Also update name if we have it
          if (displayName !== targetId) transporters[index].name = displayName;
        } else {
          transporters.push({
            id: targetId,
            name: displayName,
            arrived: status === 'present',
            notes: 'דווח דרך לשונית חופשים'
          });
        }
        
        await updateDoc(doc(db, 'transportAttendance', attendanceId), {
          transporters,
          updatedAt: new Date().toISOString(),
          timestamp: serverTimestamp()
        });
      } else {
        const allLinesSnapshot = await getDocs(collection(db, 'transportLines'));
        const transporters = allLinesSnapshot.docs.map(docSnap => {
          const lData = docSnap.data();
          const isTarget = docSnap.id === targetId;
          if (isTarget) displayName = lData.name;
          
          return {
            id: docSnap.id,
            name: lData.name,
            // If it's a holiday, default others to false. If not, default others to true.
            arrived: isTarget ? (status === 'present') : (!isHoliday),
            notes: isTarget ? 'דווח דרך לשונית חופשים' : (isHoliday ? `חופשה: ${holiday.data().name}` : '')
          };
        });

        // Add the target if it wasn't a regular line (i.e. external transporter)
        if (!transporters.find(t => t.id === targetId)) {
          transporters.push({
            id: targetId,
            name: displayName,
            arrived: status === 'present',
            notes: 'דווח דרך לשונית חופשים'
          });
        }

        await setDoc(doc(db, 'transportAttendance', attendanceId), {
          date,
          transporters,
          isHoliday: isHoliday,
          holidayName: isHoliday ? holiday.data().name : '',
          updatedAt: new Date().toISOString(),
          timestamp: serverTimestamp()
        });
      }
    }
  } catch (error) {
    if (isQuotaError(error)) return;
    console.error("Error syncing holiday attendance:", error);
  }
};

export const syncHolidayTransportBatchToRegular = async (
  date: string,
  updates: { targetId: string; targetType: string; status: 'present' | 'absent'; displayName: string }[]
) => {
  try {
    const attendanceId = `transport_${date}`;
    const { getDoc } = await import('firebase/firestore');
    
    // Find holiday info for defaults
    const holidaySnapshot = await getDocs(query(collection(db, 'holidays'), where('startDate', '<=', date)));
    const holiday = holidaySnapshot.docs.find(d => d.data().endDate >= date);
    const isHoliday = !!holiday;
    const holidayName = holiday ? holiday.data().name : '';

    const attendanceDoc = await getDoc(doc(db, 'transportAttendance', attendanceId));
    let transporters: any[] = [];

    if (attendanceDoc.exists()) {
      transporters = [...(attendanceDoc.data().transporters || [])];
    } else {
      // Initialize with all regular lines if it doesn't exist
      const allLinesSnapshot = await getDocs(collection(db, 'transportLines'));
      transporters = allLinesSnapshot.docs.map(docSnap => {
        const lData = docSnap.data();
        return {
          id: docSnap.id,
          name: lData.name,
          arrived: !isHoliday,
          notes: isHoliday ? `חופשה: ${holidayName}` : ''
        };
      });
    }

    // Apply all updates
    updates.forEach(update => {
      const index = transporters.findIndex(t => t.id === update.targetId);
      if (index >= 0) {
        transporters[index].arrived = update.status === 'present';
        if (update.displayName && update.displayName !== update.targetId) {
          transporters[index].name = update.displayName;
        }
        if (update.status === 'present') {
          transporters[index].notes = 'דווח דרך לשונית חופשים';
        } else if (isHoliday && (!transporters[index].notes || transporters[index].notes === 'דווח דרך לשונית חופשים' || transporters[index].notes === '')) {
          transporters[index].notes = `חופשה: ${holidayName}`;
        }
      } else {
        transporters.push({
          id: update.targetId,
          name: update.displayName || update.targetId,
          arrived: update.status === 'present',
          notes: update.status === 'present' ? 'דווח דרך לשונית חופשים' : (isHoliday ? `חופשה: ${holidayName}` : '')
        });
      }
    });

    await setDoc(doc(db, 'transportAttendance', attendanceId), {
      date,
      transporters,
      isHoliday,
      holidayName,
      updatedAt: new Date().toISOString(),
      timestamp: serverTimestamp()
    }, { merge: true });

    // Force refresh stats to ensure dashboard is accurate
    const { refreshDailyStats } = await import('./statsService');
    await refreshDailyStats(date, true);
  } catch (error) {
    if (isQuotaError(error)) return;
    console.error("Error batch syncing holiday transport to regular:", error);
  }
};

export const syncRegularToHoliday = async (
  targetId: string, 
  targetType: string, 
  date: string, 
  status: 'present' | 'absent',
  holidayId: string
) => {
  try {
    const docId = `${holidayId}_${date}_${targetId}`;
    
    await setDoc(doc(db, 'holidayAttendance', docId), {
      holidayId,
      date,
      targetId,
      targetType,
      status,
      lastUpdated: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn("Quota exceeded during holiday sync (ignored)");
      return;
    }
    console.error("Error syncing regular attendance to holiday:", error);
  }
};

/**
 * Batch version of syncRegularToHoliday that supports mixed target types.
 */
export const syncRegularToHolidayBatchMixed = async (
  targets: { id: string; status: 'present' | 'absent'; targetType: string }[],
  date: string,
  holidayId: string
) => {
  if (targets.length === 0) return;
  
  try {
    const { writeBatch } = await import('firebase/firestore');
    const batch = writeBatch(db);
    
    targets.forEach(target => {
      const docId = `${holidayId}_${date}_${target.id}`;
      const docRef = doc(db, 'holidayAttendance', docId);
      batch.set(docRef, {
        holidayId,
        date,
        targetId: target.id,
        targetType: target.targetType,
        status: target.status,
        lastUpdated: serverTimestamp()
      }, { merge: true });
    });
    
    await batch.commit();
  } catch (error) {
    if (isQuotaError(error)) return;
    console.error("Error syncing regular attendance to holiday (batch mixed):", error);
  }
};
